// src/modules/meal/services/recipeService.js

import authService from '../../auth/services/authService';
import { buildApiUrl } from '../../../config/api';

// Helper para manejar respuestas y redirigir en caso de 401
const handleResponse = async (response) => {
    if (response.status === 401) {
        authService.logout();
        window.location.href = '/login';
        throw new Error('Token expirado');
    }
    return response;
};

export const recipeService = {
    /**
     * Obtener todas las recetas con paginación y filtros
     */
    async getAllRecipes(page = 1, limit = 20, searchTerm = '', filters = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });

        if (searchTerm) {
            params.append('search', searchTerm);
        }

        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key].toString());
            }
        });

        const response = await fetch(buildApiUrl(`/api/user/recipes?${params}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener las recetas');
        }

        return await response.json();
    },

    /**
     * Buscar recetas
     */
    async searchRecipes(searchTerm, filters = {}) {
        const params = new URLSearchParams({
            search: searchTerm
        });

        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key].toString());
            }
        });

        const response = await fetch(buildApiUrl(`/api/user/recipes/search?${params}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al buscar recetas');
        }

        return await response.json();
    },

    /**
     * Obtener receta por ID
     */
    async getRecipeById(recipeId) {
        const response = await fetch(buildApiUrl(`/api/user/recipes/${recipeId}`), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener la receta');
        }

        return await response.json();
    },

    /**
     * Marcar/desmarcar receta como favorita
     */
    async toggleRecipeFavorite(recipeId) {
        const response = await fetch(buildApiUrl(`/api/user/recipes/${recipeId}/favorite`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al cambiar el estado de favorito');
        }

        return await response.json();
    },

    /**
     * Archivar/desarchivar receta
     */
    async archiveRecipe(recipeId) {
        const response = await fetch(buildApiUrl(`/api/user/recipes/${recipeId}/archive`), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al archivar la receta');
        }

        return await response.json();
    },

    /**
     * Actualizar receta
     */
    async updateRecipe(recipeId, data) {
        const response = await fetch(buildApiUrl(`/api/user/recipes/${recipeId}`), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al actualizar la receta');
        }

        return await response.json();
    },

    /**
     * Generar receta con IA desde una descripción
     */
    async generateRecipe(description, language = 'es') {
        const response = await fetch(buildApiUrl('/api/user/recipes/generate'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify({ description, language })
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al generar la receta');
        }

        return await response.json();
    },

    /**
     * Crear una nueva receta
     */
    async createRecipe(data) {
        const response = await fetch(buildApiUrl('/api/user/recipes'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        await handleResponse(response);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error al crear la receta');
        }

        return await response.json();
    },

    /**
     * Obtener el estado de creación de recetas (límites)
     */
    async getRecipeCreationStatus() {
        const response = await fetch(buildApiUrl('/api/user/recipes/creation-status'), {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authService.getToken()}`,
            },
            credentials: 'include',
        });

        await handleResponse(response);

        if (!response.ok) {
            throw new Error('Error al obtener el estado de creación de recetas');
        }

        return await response.json();
    }
};
